import { InterfaceLog } from '@/types/interfaceLog'
import { LogTypeEnum } from '@/types/simulation'

/**
 * Logs that record a station error, with the position of errCode used for the
 * server-side filter.
 *
 * In both types addr / idx come first and errCode / errDetail last, so they
 * are always read from the ends of the `data` array:
 * - io::on: the controller's field list names 14 fields, but most rows carry 13
 *   (switchParams is only sometimes sent), so errCode sits at 11 or 12. The
 *   filter uses 11; 14-value rows still pass it because index 11 is then
 *   verifySum, which is non-zero.
 * - io::off: addr,idx,verify,initialLoad,initialLineVoltage,finalLoad,
 *   finalLineVoltage,errCode,errDetail.
 */
export const IO_ERROR_LOG_FILTER_INDEX: Partial<Record<LogTypeEnum, number>> = {
  [LogTypeEnum.IoOn]: 11,
  [LogTypeEnum.IoOff]: 7,
}

export interface StationErrorLogDetail {
  errDetail: number
  timestamp: string
}

export const stationErrorLogKey = (
  address: string | number,
  index: string | number,
  errorCode: string | number,
) => `${address}-${index}-${errorCode}`

/**
 * Latest errDetail per decoder address + index + errCode, across io::on and
 * io::off logs. Keyed with stationErrorLogKey.
 */
export const getLatestStationErrorLogs = (
  logs: InterfaceLog[],
): Map<string, StationErrorLogDetail> => {
  const latest = new Map<string, StationErrorLogDetail & { ts: number }>()

  for (const log of logs) {
    const data = log.data ?? []
    if (IO_ERROR_LOG_FILTER_INDEX[log.type] == null || data.length < 4) continue

    const errCode = Number(data[data.length - 2])
    const errDetail = Number(data[data.length - 1])
    if (!errCode || Number.isNaN(errDetail)) continue

    const key = stationErrorLogKey(data[0], data[1], errCode)
    const current = latest.get(key)
    if (!current || log.ts > current.ts) {
      latest.set(key, { errDetail, timestamp: log.timestamp, ts: log.ts })
    }
  }

  return new Map(
    [...latest].map(([key, { errDetail, timestamp }]) => [
      key,
      { errDetail, timestamp },
    ]),
  )
}
